import {
  openInviteLink,
  parseInviteLinkLocation,
  type InviteLinkHandle,
  type SignedInvite,
} from "@palestra/core";
import { readRuntimeConfig } from "./runtime-config";

/**
 * Il riscatto del link corto: token dal percorso, chiave dal frammento, blob da
 * `GET /l/<token>`. La chiave non viaggia in nessuna richiesta HTTP — e' una
 * proprieta' del protocollo — e le verifiche restano quelle di `acceptInvite`:
 * qui si recupera soltanto l'invito, non lo si crede.
 */

export type InviteLinkRedemption =
  /** `token` serve a chiuderlo entrando: e' la stessa cassetta, non un dato nuovo. */
  | { kind: "invite"; invite: SignedInvite; token: string }
  /** `410 Gone`: scaduto o ritirato. Diverso da un guasto: riprovare non serve. */
  | { kind: "gone" }
  /**
   * `410` con `claimed`: la cassetta c'era, ma qualcun altro e' gia' entrato con
   * questo link. E' l'unico esito che riguarda una persona invece di un tempo,
   * e va detto con quelle parole.
   */
  | { kind: "claimed" }
  /** Il relay non ha risposto: si puo' riprovare dallo stesso link. */
  | { kind: "unreachable" }
  /** Il blob c'e' ma non si apre: link monco o manomesso. */
  | { kind: "invalid" };

/**
 * L'esito dell'ingresso dichiarato. `taken` ferma chi entra; `unreachable` no:
 * un relay muto non deve impedire di entrare, perche' l'app resta utilizzabile
 * offline e la difesa vera — `spentInvites`, firmata — arriva comunque.
 */
export type InviteLinkClaimOutcome = "ok" | "taken" | "unreachable";

export function inviteLinkFromLocation(): InviteLinkHandle | undefined {
  return parseInviteLinkLocation(location);
}

/**
 * In produzione la cassetta sta sulla stessa origine e l'edge Go serve `/l/*`.
 * In sviluppo il relay ascolta da solo sulla 8787.
 */
function inviteBlobUrl(token: string, suffix = ""): string {
  const path = `/l/${token}${suffix}`;
  if (readRuntimeConfig() || !import.meta.env.DEV) return path;
  const developmentUrl = import.meta.env.VITE_RELAY_URL as string | undefined;
  if (developmentUrl) {
    try {
      const url = new URL(developmentUrl);
      url.protocol = url.protocol === "wss:" ? "https:" : "http:";
      url.pathname = path;
      url.hash = "";
      url.search = "";
      return url.href;
    } catch {
      // Un valore malformato non deve rompere il riscatto: si ripiega sotto.
    }
  }
  return `http://${location.hostname}:8787${path}`;
}

export async function redeemInviteLink(link: InviteLinkHandle): Promise<InviteLinkRedemption> {
  let response: Response;
  try {
    response = await fetch(inviteBlobUrl(link.token), {
      headers: { accept: "application/json" },
    });
  } catch {
    return { kind: "unreachable" };
  }
  if (response.status === 410) {
    return (await goneReason(response)) === "claimed" ? { kind: "claimed" } : { kind: "gone" };
  }
  if (!response.ok) return { kind: "unreachable" };
  try {
    const payload = (await response.json()) as { blob?: unknown };
    if (typeof payload.blob !== "string") return { kind: "invalid" };
    return {
      kind: "invite",
      invite: await openInviteLink({ ...link, blob: payload.blob }),
      token: link.token,
    };
  } catch {
    return { kind: "invalid" };
  }
}

/** Un corpo illeggibile vale «scaduto»: e' il motivo che il relay dava prima. */
async function goneReason(response: Response): Promise<string | undefined> {
  try {
    const payload = (await response.json()) as { error?: unknown };
    return typeof payload.error === "string" ? payload.error : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Chiude il link nell'istante dell'ingresso. Va chiamato *prima* di creare il
 * profilo: il costo di un link bruciato per un ingresso che poi fallisce e'
 * molto minore di quello di due persone che credono entrambe di essere entrate,
 * e solo la seconda si accorgera' — dopo giorni — di non esserci mai stata.
 */
export async function claimInviteLink(token: string): Promise<InviteLinkClaimOutcome> {
  let response: Response;
  try {
    response = await fetch(inviteBlobUrl(token, "/claim"), {
      method: "POST",
      headers: { accept: "application/json" },
    });
  } catch {
    return "unreachable";
  }
  if (response.ok) return "ok";
  /**
   * `410 claimed` e' l'unico rifiuto che ferma: «scaduto» lo dira' comunque
   * `verifyInvite`, e un guasto del relay non deve chiudere la porta a chi ha
   * un invito buono in mano.
   */
  if (response.status === 410 && (await goneReason(response)) === "claimed") return "taken";
  return "unreachable";
}
